<template>
  <main class="main weapon">
    <div class="f container">
      <LazyBreadcrumbs
        v-if="weapon"
        :item="weapon"
        type="item"
      />
      <button
        :class="{ compressed }"
        aria-label="compressed button"
        class="compress-btn ht hd"
        @click="toggleCompressed"
      >
        <svg>
          <use
            v-if="compressed"
            xlink:href="/icons/sprite.svg#grid-1"
          />
          <use
            v-else
            xlink:href="/icons/sprite.svg#grid-2"
          />
        </svg>
      </button>
    </div>

    <LazyPagesContainer
      :compressed="compressed"
      :data="data?.data?.itemsContainerPage || {} as ItemsContainerPageResult"
      :is-rare="true"
      :pending="status === 'pending'"
    />

    <div
      v-if="status === 'pending'"
      class="load_period loading-container mt-10"
    >
      <div class="f fc fjc">
        <i class="i-loading ml10" />
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { ItemsByField, ItemsContainerPageResult } from '~/graphql/generated/schema-types';
import { useAlternateLinks } from '~/composables/alternate-links';
import { useDefineProduct } from '~/composables/define-product';
import { getItemsContainerPage } from '~/utils/api';

const route = useRoute();
const { compressed, toggleCompressed } = useCompressed();
const { t, locale } = useI18n();
const { defineUseProduct } = useDefineProduct();

const {
  error,
  data,
  status,
} = await useAsyncData(`getItemsContainerPage${route.params.id}`, () => getItemsContainerPage(route.params.id as string, true));

if (error.value || data.value?.errors?.length) {
  if (process.client) {
    window.location.reload();
  }
  console.log(`Containers rare items page fetch error: ${error.value?.message || data.value?.errors?.[0]?.message}. path: ${route.path}`);
  throw createError({
    status: 404,
    statusText: `Containers rare items page fetch error: ${error.value?.message || data.value?.errors?.[0]?.message}. path: ${route.path}`,
  });
}

if (data.value?.data?.itemsContainerPage?.is_collection) {
  await navigateTo(`/collections` + `/${route.params.id}`, { redirectCode: 301 });
}
const items = computed(() => {
  const localeData = (data.value?.data?.itemsContainerPage || {}) as ItemsContainerPageResult;
  return localeData?.result as ItemsByField[];
});

const weapon = computed(() => {
  const name = route.params.id as string;
  const localedName = getModifiedString(data.value?.data?.itemsContainerPage?.localed_name || '');
  let category: string = '';

  if (name.includes('souvenir') || localedName.includes('souvenir')) {
    category = 'souvenir';
  } else if (name.includes('autograph-capsule') || localedName.includes('autograph-capsule')) {
    category = 'Autograph Capsule';
  } else if (name.includes('collection-package') || localedName.includes('collection-package')) {
    category = 'Crates';
  } else if (name.includes('graffiti') || localedName.includes('graffiti')) {
    category = 'Graffiti';
  } else if (name.includes('music-kit-box') || localedName.includes('music-kit-box')) {
    category = 'Music Kit Box';
  } else if (name.includes('case') || localedName.includes('case')) {
    category = 'Case';
  } else if (name.includes('sticker') || localedName.includes('sticker')) {
    category = 'Sticker capsule';
  } else if (name.includes('pin') || localedName.includes('pin')) {
    category = 'Pins';
  } else if (name.includes('patch') || localedName.includes('patch')) {
    category = 'Patch Capsule';
  } else if (name.includes('collection') || localedName.includes('collection')) {
    category = 'Collections';
  }

  return {
    market_hash_name: data.value?.data?.itemsContainerPage?.localed_name || name,
    weapon: category,
  } as any;
});

const weaponTitle = computed(() => {
  const name = data.value?.data?.itemsContainerPage?.localed_name;
  return t('all_items', {
    name: `${name}`,
    items: t('filters.weapon.Knives'),
    // items: weapon.value.category === 'Case' || weapon.value.category === 'Crates' ? t('Skins') : t('items')0
  });
});

const weaponDescription = computed(() => {
  return t('heads.item.description', { name: getItemNameOnly(weapon.value.market_hash_name || '') });
});

const weaponIcon = computed(() => {
  return getImageFromWaxpeer(weapon.value?.market_hash_name || '');
});

defineOgImageComponent('Dynamic', {
  imageUrl: getImageFromWaxpeer(weapon.value?.market_hash_name || '', '1200:600').replace('f:webp', 'f:png'),
});

const { alternateLinks, canonical } = useAlternateLinks(`/containers/${route.params.id}/rare-items`);

useHead({
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items',
    },
  ],
  link: () => alternateLinks.value,
});

useSeoMeta({
  title: () => weaponTitle.value,
  ogTitle: () => weaponTitle.value,
  description: () => weaponDescription.value,
  ogDescription: () => weaponDescription.value,
  // ogImage: () => weaponIcon.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => weaponTitle.value,
  twitterDescription: () => weaponDescription.value,
  // twitterImage: () => weaponIcon.value,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

useSchemaOrg([
  defineItemList({
    name: weaponTitle.value,
    description: weaponDescription.value,
    image: () => weaponIcon.value,
    itemListElement: await Promise.all(items.value.map(async (weapon, index) => {
      const product = await defineUseProduct(weapon as ItemsByField & {
        rating: number
        votes: number
      });

      return {
        '@type': 'ListItem',
        'position': index + 1,
        'item': product,
        'id': product.id || `item-${index + 1}-${weapon?.market_hash_name}`,
      };
    })),
  }),
  defineBreadcrumb({
    itemListElement: [
      { name: () => t('main'), item: '/' },
      {
        name: `${getWeaponOnly(weapon.value.market_hash_name)}(weapon page)`,
        item: getWeaponLink(weapon.value?.weapon || ''),
      },
      {
        name: `${getItemNameOnly(weapon.value.market_hash_name)} (item page)`,
        item: getItemLinkWithExterior({
          name: weapon.value?.market_hash_name || '',
          slug: weapon.value?.link,
        }),
      },
      {
        name: `${getItemNameOnly(weapon.value.market_hash_name)} (containers page)`,
        item: `${getContainerLink({
          name: weapon.value?.market_hash_name || '',
          slug: weapon.value?.link,
        },
        )}/rate-items`,
      },
    ],
  }),
]);
</script>

<style lang="scss" scoped>
.weapon {
  padding: 15rem 2rem 4rem;
  margin: 0 auto;
  width: 100%;

  .compress-btn {
    width: 40px;
    height: 40px;
    padding: 5px;
    border: 1px solid var(--gray);

    &.compressed {
      padding: 8px;
    }

    svg {
      width: 100%;
      height: 100%;
    }
  }

  @media screen and (max-width: 1115px) {
    padding-top: 9rem;
  }
  @media screen and (max-width: 979px) {
    padding-top: 7rem;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
